import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Users, AlertTriangle, RotateCw, ChevronRight, ChevronLeft } from "lucide-react";
import { api } from "../lib/api";
import { StatusBadge, SimBadge } from "../components/Badges";
import { CinematicPortrait } from "../components/CinematicPortrait";
import { CountryFilterRow } from "../components/CountryFilterRow";
import { Flag } from "../components/Flag";

const CONTAINER = "mx-auto max-w-[1700px] px-4 sm:px-8 lg:px-12";
const PAGE_SIZE = 50;

function RegistrySkeletonCard() {
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.14)] p-5 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-[#1a1407]" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-2/3 bg-[#1a1407] rounded" />
          <div className="h-3 w-1/2 bg-[#141414] rounded" />
          <div className="h-3 w-1/3 bg-[#141414] rounded" />
        </div>
      </div>
    </div>
  );
}

function splitName(name) {
  const n = (name || "").trim();
  const idx = n.lastIndexOf(" ");
  if (idx === -1) return [n, ""];
  return [n.slice(0, idx), n.slice(idx + 1)];
}

function locationLabel(p) {
  const city = (p.city || "").trim();
  const country = (p.country?.name || "").trim();
  if (city && country && city.toLowerCase() !== country.toLowerCase()) return `${city}, ${country}`;
  return city || country;
}

function PlayerCard({ p }) {
  const [first, last] = splitName(p.display_name);
  return (
    <Link
      to={`/player/${p.wsbf_player_id}`}
      data-testid={`player-card-${p.wsbf_player_id}`}
      className="group relative rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.16)] p-5 flex items-start gap-4 hover:border-[#E5B842]/55"
      style={{ transition: "border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 30px -12px rgba(212,175,55,0.35)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      <CinematicPortrait player={p} variant="card" className="shrink-0 w-12 h-[60px]" testid={`player-avatar-${p.wsbf_player_id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <Flag country={p.country} width={20} className="shrink-0 mt-1" testid={`player-flag-${p.wsbf_player_id}`} />
          <span data-testid={`player-name-${p.wsbf_player_id}`} className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50 leading-tight min-w-0 flex-1 break-words">
            {first}{last && <><br />{last}</>}
          </span>
          {p.is_simulated && <span className="shrink-0 mt-0.5"><SimBadge /></span>}
        </div>
        <div className="font-mono text-[11px] text-[#E5B842] tracking-widest mt-0.5">{p.wsbf_player_id}</div>
        <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400">
          <span className="truncate" data-testid={`player-location-${p.wsbf_player_id}`}>{locationLabel(p)}</span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <StatusBadge status={p.status} />
          {p.position && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider bg-[#141414] border border-[#2b2512] text-slate-400">
              {p.position}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#E5B842] shrink-0" style={{ transition: "color 0.18s ease" }} />
    </Link>
  );
}

function pageWindow(current, totalPages) {
  // Compact windowed page list with ellipses.
  const pages = new Set([1, totalPages, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

function Pager({ page, totalPages, total, onGo }) {
  if (totalPages <= 1) return (
    <div className="mt-8 text-center text-xs font-mono text-slate-500" data-testid="players-pager-info">Page 1 of 1 · {total} players</div>
  );
  const win = pageWindow(page, totalPages);
  const btn = "inline-flex items-center justify-center min-w-[38px] h-[38px] px-3 rounded-lg text-sm font-bold border";
  return (
    <div className="mt-8 flex flex-col items-center gap-3" data-testid="players-pager">
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button onClick={() => onGo(page - 1)} disabled={page <= 1} data-testid="players-prev"
          className={`${btn} bg-[#161616] border-[rgba(212,175,55,0.22)] text-slate-300 hover:border-[#E5B842]/60 hover:text-[#E5B842] disabled:opacity-40 disabled:cursor-not-allowed`} style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
          <ChevronLeft className="w-4 h-4" />
        </button>
        {win.map((p, i) => p === "…" ? (
          <span key={`e${i}`} className="px-2 text-slate-600 font-mono">…</span>
        ) : (
          <button key={p} onClick={() => onGo(p)} data-testid={`players-page-${p}`}
            className={`${btn} ${p === page ? "bg-[#E5B842] text-black border-[#E5B842]" : "bg-[#161616] border-[rgba(212,175,55,0.22)] text-slate-300 hover:border-[#E5B842]/60 hover:text-[#E5B842]"}`} style={{ transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease" }}>
            {p}
          </button>
        ))}
        <button onClick={() => onGo(page + 1)} disabled={page >= totalPages} data-testid="players-next"
          className={`${btn} bg-[#161616] border-[rgba(212,175,55,0.22)] text-slate-300 hover:border-[#E5B842]/60 hover:text-[#E5B842] disabled:opacity-40 disabled:cursor-not-allowed`} style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="text-xs font-mono text-slate-500" data-testid="players-pager-info">Page {page} of {totalPages} · {total} players</div>
    </div>
  );
}

export default function PlayersList() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("search") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [countries, setCountries] = useState([]);
  const [totalAll, setTotalAll] = useState(0);

  const search = sp.get("search") || "";
  const paramCountry = sp.get("country");
  const page = Math.max(1, parseInt(sp.get("page") || "1", 10) || 1);

  // Country facets — dynamic, same semantics as the list endpoint.
  useEffect(() => {
    api.get("/players/countries").then(({ data }) => {
      setCountries(data.countries || []);
      setTotalAll(data.total || 0);
    }).catch(() => { setCountries([]); setTotalAll(0); });
  }, []);

  const hasGreece = countries.some((c) => c.code === "GRE");
  const selected = paramCountry
    ? paramCountry.toUpperCase()
    : (countries.length === 0 ? "GRE" : hasGreece ? "GRE" : "ALL");

  // Keep q in sync if the URL search param changes externally.
  useEffect(() => { setQ(sp.get("search") || ""); }, [sp]);

  const setParams = useCallback((updates) => {
    const next = new URLSearchParams(sp);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, v);
    });
    setSp(next, { replace: true });
  }, [sp, setSp]);

  const selectCountry = (code) => setParams({ country: code, page: null });
  const goPage = (p) => setParams({ page: String(p) });

  // Debounce search typing into the URL (resets to page 1).
  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim() !== search) setParams({ search: q.trim() || null, page: null });
    }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    setData(null);
    setError(false);
    const parts = [`page=${page}`, `page_size=${PAGE_SIZE}`];
    if (search) parts.push(`search=${encodeURIComponent(search)}`);
    if (selected && selected !== "ALL") parts.push(`country=${encodeURIComponent(selected)}`);
    api.get(`/players?${parts.join("&")}`)
      .then(({ data }) => {
        setData(data);
        // If the query resolved to exactly one country, sync the chip so the UI reflects it.
        const mc = data?.matched_countries || [];
        if (mc.length === 1 && mc[0] !== selected) setParams({ country: mc[0], page: null });
      })
      .catch(() => setError(true));
  }, [search, selected, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedName = countries.find((c) => c.code === selected)?.name;

  return (
    <div className="animate-fade-up pb-16">
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 140% at 85% 20%, rgba(212,175,55,0.14) 0%, rgba(212,175,55,0.04) 30%, transparent 60%)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-16`}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3">Global Registry</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">Players</h1>
          <p className="text-slate-400 mt-3 text-sm sm:text-base max-w-2xl">Search sanctioned Arena Players by name, WSTF Player ID or country.</p>
          {data && (
            <div data-testid="registry-total" className="mt-5 inline-flex items-center gap-2 text-sm font-mono text-slate-300">
              <Users className="w-4 h-4 text-[#E5B842]" />
              <span className="font-heading text-xl font-black text-[#E5B842] tabular-nums">{total}</span>
              <span className="uppercase tracking-widest text-slate-500 text-xs">
                {selected === "ALL" ? "Registered Players" : `Players in ${selectedName || selected}`}
              </span>
            </div>
          )}
        </div>
      </section>

      <div className={`${CONTAINER} pt-8`}>
        <div className="relative max-w-3xl">
          <Search className="w-5 h-5 text-[#E5B842]/70 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="players-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, WSTF Player ID or country"
            className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-xl pl-12 pr-4 py-4 text-slate-100 text-base placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/70 focus:ring-2 focus:ring-[#E5B842]/25"
            style={{ transition: "border-color 0.15s ease, box-shadow 0.15s ease" }}
          />
        </div>

        {countries.length > 0 && (
          <CountryFilterRow countries={countries} selected={selected} totalAll={totalAll} onSelect={selectCountry} />
        )}

        {search && (
          <div className="mt-3 text-xs font-mono text-slate-500">
            Showing results for “<span className="text-[#E5B842]">{search}</span>”
            <button onClick={() => { setQ(""); setParams({ search: null, page: null }); }} data-testid="players-clear-search" className="ml-2 text-slate-400 hover:text-[#E5B842] underline">clear</button>
          </div>
        )}
      </div>

      <div className={`${CONTAINER} pt-8`}>
        {error ? (
          <div data-testid="players-error" className="text-center py-20 border border-dashed border-rose-500/30 rounded-xl">
            <AlertTriangle className="w-7 h-7 text-rose-400 mx-auto mb-3" />
            <div className="text-slate-300 font-mono text-sm uppercase tracking-widest mb-4">Could not load the registry</div>
            <button onClick={load} data-testid="players-retry" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[rgba(212,175,55,0.3)] text-slate-200 hover:border-[#E5B842]/60 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
              <RotateCw className="w-4 h-4" /> Retry
            </button>
          </div>
        ) : !data ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="players-skeleton">
            {Array.from({ length: 12 }).map((_, i) => <RegistrySkeletonCard key={i} />)}
          </div>
        ) : data.players.length === 0 ? (
          <div data-testid="players-empty" className="text-center py-20 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-xl">
            {search ? "No players match this search." : selected !== "ALL" && selectedName ? `No players in ${selectedName} yet.` : "No players found"}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="players-grid">
              {data.players.map((p) => <PlayerCard key={p.id} p={p} />)}
            </div>
            <Pager page={page} totalPages={totalPages} total={total} onGo={(p) => goPage(Math.min(Math.max(1, p), totalPages))} />
          </>
        )}
      </div>
    </div>
  );
}
